import ScreenLockOverlay from '@/components/ScreenLockOverlay.vue'

/**
 * Global screen lock composable for preventing user interaction during async operations.
 * Provides a non-dismissible overlay with optional loading spinner and message.
 *
 * ## Features
 * - Non-dismissible overlay (prevents clicks, ESC key, and all user interaction)
 * - Automatic scroll lock while active
 * - Optional custom message display
 * - Auto-unlock on route change (by default)
 * - Configurable timeout with auto-unlock safeguard (default: 10 seconds)
 * - SSR-safe (client-only execution)
 *
 * ## Default Behavior
 * - **Non-dismissible**: Users cannot close the overlay by clicking or pressing ESC
 * - **Auto-unlock on navigation**: Lock automatically clears when route changes (unless `persistOnRouteChange: true`)
 * - **Timeout safeguard**: Automatically unlocks after 10 seconds (configurable in `app.config.ts` or per-call via `timeoutMs`)
 * - **Idempotent**: Subsequent `lock()` calls while already locked are ignored
 *
 * @example
 * Basic usage with error handling:
 * ```ts
 * const { lock, unlock } = useScreenLock()
 *
 * try {
 *   lock({ message: 'Adding to cart...' })
 *   await addToCart()
 *   // Lock persists until navigation (or manual unlock on error)
 * } catch (error) {
 *   unlock() // Unlock on error to allow user to retry
 *   showError(error)
 * }
 * ```
 *
 * @example
 * Using with route change persistence:
 * ```ts
 * lock({
 *   message: 'Processing...',
 *   persistOnRouteChange: true // Lock persists even after navigation
 * })
 * ```
 *
 * @example
 * Custom timeout per call:
 * ```ts
 * lock({
 *   message: 'Long operation...',
 *   timeoutMs: 30000 // 30 seconds
 * })
 * ```
 */

/**
 * Options for configuring the screen lock behavior
 */
interface ScreenLockOptions {
    /** Optional message to display to the user below the spinner */
    message?: string
    /**
     * Whether to persist lock when route changes (default: false)
     * When false, the lock automatically clears on route navigation
     */
    persistOnRouteChange?: boolean
    /**
     * Timeout in milliseconds before auto-unlock (default: 10 seconds)
     * Default value comes from `app.config.ts` -> `screenLock.defaultTimeoutMs`
     * Set to prevent locks from persisting indefinitely if unlock() is never called
     */
    timeoutMs?: number
    /**
     * Z-index for the overlay (default: highest available)
     * Use to ensure the overlay appears above other UI elements
     */
    zIndex?: number
    /**
     * Block interaction without painting the scrim, spinner, or message (default: false).
     * Use when the triggering button already shows its own loading state.
     */
    transparent?: boolean
}

let isLocked = false
let overlayFactory: ReturnType<ReturnType<typeof useOverlay>['create']> | null = null
let overlayManager: ReturnType<typeof useOverlay> | null = null
let openedOverlayId: symbol | null = null
let timeoutId: ReturnType<typeof setTimeout> | null = null
let routeWatcher: ReturnType<typeof watch> | null = null
let persistOnRouteChange = false

/**
 * Composable function that returns lock and unlock methods for controlling the screen lock overlay.
 *
 * The composable uses a singleton pattern internally to ensure only one lock can be active at a time.
 * All calls to `useScreenLock()` share the same lock state across the application.
 *
 * @returns An object containing `lock` and `unlock` methods
 *
 * @example
 * ```ts
 * const { lock, unlock } = useScreenLock()
 *
 * // Lock the screen
 * await lock({ message: 'Loading...' })
 *
 * // Unlock when done
 * unlock()
 * ```
 */
export function useScreenLock() {
    // Initialize overlay manager and factory if not already created
    if (!overlayManager || !overlayFactory) {
        overlayManager = useOverlay()
        overlayFactory = overlayManager.create(ScreenLockOverlay)
    }

    const screenLock = overlayFactory!

    /**
     * Locks the screen with a non-dismissible overlay and optional configuration.
     *
     * Opens an overlay that prevents all user interaction until `unlock()` is called,
     * the route changes (unless `persistOnRouteChange: true`), or the timeout expires.
     *
     * If the screen is already locked, subsequent calls to `lock()` are ignored (idempotent).
     *
     * @param options - Configuration options for the lock behavior
     * @param options.message - Optional message to display below the spinner
     * @param options.persistOnRouteChange - If true, lock persists across route changes (default: false)
     * @param options.timeoutMs - Custom timeout in milliseconds (default: from app.config.ts or 10s)
     * @param options.zIndex - Custom z-index for the overlay (default: highest available)
     *
     * @returns Promise that resolves when the lock is established
     *
     * @example
     * ```ts
     * // Basic lock with message
     * await lock({ message: 'Adding to cart...' })
     *
     * // Lock that persists across navigation
     * await lock({
     *   message: 'Processing...',
     *   persistOnRouteChange: true
     * })
     * ```
     */
    const lock = async (options: ScreenLockOptions = {}): Promise<void> => {
        // SSR guard: only run on client
        if (!import.meta.client) {
            return
        }

        // Ignore subsequent lock() calls while already locked
        if (isLocked) {
            return
        }

        isLocked = true
        persistOnRouteChange = options.persistOnRouteChange ?? false

        // Open the overlay and store the opened instance
        // overlayFactory is guaranteed to be non-null here due to the check above

        const openedInstance = screenLock.open({
            message: options.message,
            zIndex: options.zIndex,
            transparent: options.transparent,
        })

        // Store the opened instance so we can close it programmatically
        openedOverlayId = openedInstance.id

        // Set up timeout to auto-unlock (default from app.config)
        const appConfig = useAppConfig()
        const defaultTimeoutMs = appConfig.screenLock?.defaultTimeoutMs ?? 10000
        const timeoutMs = options.timeoutMs ?? defaultTimeoutMs
        timeoutId = setTimeout(() => {
            unlock()
        }, timeoutMs)

        // Set up route watcher to auto-unlock on route change (unless persistOnRouteChange is true)
        if (!persistOnRouteChange) {
            const route = useRoute()
            routeWatcher = watch(
                () => route.path,
                () => {
                    // Route changed, unlock the screen
                    unlock()
                },
                { once: true }
            )
        }
    }

    /**
     * Unlocks the screen and cleans up all associated resources.
     *
     * Closes the overlay, clears the timeout, and stops the route watcher.
     * Safe to call multiple times (idempotent) - subsequent calls are ignored if not locked.
     *
     * @returns void
     *
     * @example
     * ```ts
     * // Unlock after operation completes
     * try {
     *   await someAsyncOperation()
     * } finally {
     *   unlock() // Always unlock, even on error
     * }
     * ```
     */
    const unlock = (): void => {
        // SSR guard: only run on client
        if (!import.meta.client) {
            return
        }

        if (!isLocked) {
            return
        }

        isLocked = false
        persistOnRouteChange = false

        // Clear timeout if it exists
        if (timeoutId) {
            clearTimeout(timeoutId)
            timeoutId = null
        }

        // Stop route watcher if it exists
        if (routeWatcher) {
            routeWatcher()
            routeWatcher = null
        }

        // Close overlay instance if it exists
        if (openedOverlayId && overlayManager) {
            overlayManager.close(openedOverlayId)
            openedOverlayId = null
        }
    }

    return {
        lock,
        unlock,
    }
}
